import React from 'react';
import themeStyles from '../photoEditorTheme.module.css';

interface ImagePreviewProps {
  imageUrl: string;
  filename: string;
  onRemove: () => void;
}

interface FileInputProps {
  inputRef: React.RefObject<HTMLInputElement | null>;
  accept?: string;
  disabled?: boolean;
  id: string;
  ariaLabel: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface UploadButtonProps {
  label: string;
  buttonClasses: string;
  isLoading: boolean;
  disabled: boolean;
  onClick: () => void;
  ariaLabel: string;
  showGreenTick?: boolean;
}

interface NoFileChosenProps {
  hasFile: boolean;
}

/**
 * Remove button SVG icon component
 */
export function RemoveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.95001 5.3839C5.79381 5.22768 5.54054 5.22766 5.38432 5.38387C5.22811 5.54007 5.22809 5.79333 5.38429 5.94955L7.43483 8.00031L5.38479 10.0506C5.22859 10.2068 5.2286 10.46 5.38482 10.6162C5.54104 10.7724 5.79431 10.7724 5.95051 10.6162L8.00049 8.56603L10.0505 10.6162C10.2067 10.7724 10.4599 10.7724 10.6161 10.6162C10.7724 10.46 10.7724 10.2068 10.6162 10.0506L8.56614 8.00032L10.6167 5.94955C10.7729 5.79333 10.7729 5.54007 10.6167 5.38387C10.4605 5.22766 10.2072 5.22768 10.051 5.3839L8.00049 7.4346L5.95001 5.3839Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.00002 0.93335C4.0972 0.93335 0.93335 4.0972 0.93335 8.00002C0.93335 11.9028 4.0972 15.0667 8.00002 15.0667C11.9028 15.0667 15.0667 11.9028 15.0667 8.00002C15.0667 4.0972 11.9028 0.93335 8.00002 0.93335ZM1.73335 8.00002C1.73335 4.53903 4.53903 1.73335 8.00002 1.73335C11.461 1.73335 14.2667 4.53903 14.2667 8.00002C14.2667 11.461 11.461 14.2667 8.00002 14.2667C4.53903 14.2667 1.73335 11.461 1.73335 8.00002Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * Loading spinner component
 */
export function LoadingSpinner() {
  return (
    <span className="loading__spinner">
      <svg className="spinner" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle
          className="path"
          cx="10"
          cy="10"
          r="8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="50"
          strokeDashoffset="20"
        />
      </svg>
    </span>
  );
}

/**
 * Green tick icon component
 */
export function GreenTick() {
  return (
    <span className={themeStyles['photo-editor__green-tick']} aria-label="All files uploaded">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M16.6667 5L7.50004 14.1667L3.33337 10"
          stroke="#22C55E"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

/**
 * Image preview component
 */
export function ImagePreview({ imageUrl, filename, onRemove }: ImagePreviewProps) {
  return (
    <div className={themeStyles['photo-editor__preview']}>
      <div className={themeStyles['photo-editor__preview-container']}>
        <div className={themeStyles['photo-editor__preview-icon']}>
          <img src={imageUrl} alt="File preview" className={themeStyles['photo-editor__preview-image']} />
        </div>
        <div className={themeStyles['photo-editor__preview-content']}>
          <span className={themeStyles['photo-editor__preview-filename'] + ' body-sm'}>{filename}</span>
        </div>
        <button
          type="button"
          className={themeStyles['photo-editor__preview-remove'] + ' button-reset'}
          onClick={onRemove}
          aria-label="Remove image"
        >
          <span className={'body-xs ' + themeStyles['photo-editor__preview-remove-text']}>Remove</span>
          <span
            className={themeStyles['photo-editor__preview-remove-icon-wrapper'] + ' svg-wrapper svg-wrapper--small'}
          >
            <RemoveIcon />
          </span>
        </button>
      </div>
    </div>
  );
}

/**
 * Hidden file input component
 */
export function FileInput({ inputRef, accept, disabled, id, ariaLabel, onChange }: FileInputProps) {
  return (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      onChange={onChange}
      className={themeStyles['photo-editor__input']}
      disabled={disabled}
      aria-label={ariaLabel}
      id={id}
    />
  );
}

/**
 * Upload button component with optional green tick
 */
export function UploadButton({
  label,
  buttonClasses,
  isLoading,
  disabled,
  onClick,
  ariaLabel,
  showGreenTick = false
}: UploadButtonProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <button
        type="button"
        className={buttonClasses}
        onClick={onClick}
        disabled={disabled || isLoading}
        aria-label={ariaLabel}
      >
        {isLoading ? <LoadingSpinner /> : <>{label}</>}
      </button>
      {showGreenTick && <GreenTick />}
    </div>
  );
}

/**
 * "No file chosen" message component
 */
export function NoFileChosen({ hasFile }: NoFileChosenProps) {
  if (hasFile) {
    return null;
  }

  return <p className={themeStyles['photo-editor__no-file-chosen'] + ' body-sm'}>No file chosen</p>;
}
